import { Match } from "./../models/match";
import { parseISO } from "date-fns";
import axios from "@/common/services/axios";
import { GROUPS_ENDPOINT } from "@/common/constants";
import { Player } from "@/common/models/player";
import { Bet, BetPublicResponse, BetRequest, BetResponse, BetStatus } from "@/common/models/bet";
import { Group } from "@/common/models/group";

const processBet = (raw: BetResponse): Bet => ({
  id: raw.id,
  player: raw.player_id,
  match: raw.match_id,
  results: raw.results.map((r) => ({ team: r.team_id, result: r.result })),
  result: raw.results.reduce((acc, cur) => {
    acc[cur.team_id] = cur.result;
    return acc;
  }, {} as { [id: string]: number }),
  score: raw.score,
  betTime: parseISO(raw.bet_time),
  status: raw.status as unknown as BetStatus,
  created: parseISO(raw.created_at),
  updated: parseISO(raw.updated_at),
  createdBy: raw.created_by,
  updatedBy: raw.updated_by,
});

const processBetPublic = (raw: BetPublicResponse): Bet => ({
  id: raw.id,
  player: {
    user: {
      id: raw.user.id,
      alias: raw.user.alias,
      avatar: raw.user.avatar,
    },
  } as Player,
  match: raw.match_id,
  results: raw.results.map((r) => ({ team: r.team_id, result: r.result })),
  result: raw.results.reduce((acc, cur) => {
    acc[cur.team_id] = cur.result;
    return acc;
  }, {} as { [id: string]: number }),
  score: raw.score,
  betTime: parseISO(raw.bet_time),
  status: raw.status as unknown as BetStatus,
  created: parseISO(raw.created_at),
  updated: parseISO(raw.updated_at),
  createdBy: raw.created_by,
  updatedBy: raw.updated_by,
});

export const fetchPlayerBets = async (player: Player): Promise<Bet[]> => {
  const response = await axios.get(`${GROUPS_ENDPOINT}/${player.group}/players/${player.id}/bets`);
  return Array.isArray(response.data)
    ? response.data.map((raw: BetResponse) => processBet(raw))
    : [];
};

export const fetchPlayerMeBets = async (group: Group): Promise<Bet[]> => {
  const response = await axios.get(`${GROUPS_ENDPOINT}/${group.id}/players/me/bets`);
  return Array.isArray(response.data)
    ? response.data.map((raw: BetResponse) => processBet(raw))
    : [];
};

export const createBet = async (bet: BetRequest, group: Group): Promise<Bet> => {
  const url = `${GROUPS_ENDPOINT}/${group.id}/players/me/bets`;
  // eslint-disable-next-line camelcase
  const { match_id, results } = bet;
  const payload = {
    // eslint-disable-next-line camelcase
    match_id,
    results,
  };
  const response = await axios.post(url, payload);
  return processBet(response.data);
};

export const updateBet = async (bet: BetRequest, group: Group): Promise<Bet> => {
  const { id, results } = bet;
  const url = `${GROUPS_ENDPOINT}/${group.id}/players/me/bets/${id}`;
  const payload = {
    results,
  };
  const response = await axios.put(url, payload);
  return processBet(response.data);
};

export const fetchPublicPlayerBets = async (player: Player): Promise<Bet[]> => {
  const response = await axios.get(
    `${GROUPS_ENDPOINT}/${player.group}/players/${player.id}/publicbets`,
  );
  return Array.isArray(response.data)
    ? response.data.map((raw: BetResponse) => processBet(raw))
    : [];
};

export const fetchPublicMatchBets = async (match: Match, group: Group): Promise<Bet[]> => {
  const response = await axios.get(
    `${GROUPS_ENDPOINT}/${group.id}/matches/${match.id}/bets?orderBy=score+desc`,
  );
  return Array.isArray(response.data)
    ? response.data.map((raw: BetPublicResponse) => processBetPublic(raw))
    : [];
};
